import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import { Button, Skeleton } from "@mui/material";
import {
  AccessTimeOutlined,
  CalendarMonthOutlined,
  CheckCircleOutline,
  DownloadOutlined,
  LocationOnOutlined,
  BadgeOutlined,
  PersonOutline,
  PrintOutlined,
  SchoolOutlined,
  ScheduleOutlined,
} from "@mui/icons-material";

import ConfirmDialog from "components/atoms/ConfirmDialog/ConfirmDialog";
import {
  findAcaoQualificacao,
  findTurmasDisponiveisAcao,
  realizarInscricaoTurma,
} from "../../../API/apiAcoes";
import { TurmaDisponivelInscricaoDetalhe } from "../../../types/acao";
import {
  formatarCpf,
  formatarDataDbParaBr,
  formatarDatetimeCompletoBr,
} from "../../../utils/Masks";
import { montarEnderecoTurma } from "../../../utils/turmaEndereco";
import { useAuth } from "../../../resources/AuthProvider";
import {
  formatarHoraComprovante,
  imprimirComprovanteInscricao,
  salvarPdfComprovanteInscricao,
} from "./utils/comprovanteInscricao";

import styles from "./InscricaoAcao.module.css";
import stylesAcoes from "./AcoesDisponiveis.module.css";
import styles_button from "../../../styles/Button.module.css";

const obterLinkComprovante = (
  turma: TurmaDisponivelInscricaoDetalhe,
): string | null => {
  const modalidade = turma.descricao_modalidade
    ?.trim()
    .toLocaleLowerCase("pt-BR");
  return modalidade !== "presencial" && turma.link?.trim()
    ? turma.link.trim()
    : null;
};

const EscolherTurma = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const acaoId = searchParams.get("id");

  const [loading, setLoading] = useState<boolean>(true);
  const [msgError, setMsgError] = useState<string | null>(null);

  const [acaoNome, setAcaoNome] = useState<string>("");
  const [turmas, setTurmas] = useState<TurmaDisponivelInscricaoDetalhe[]>([]);

  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [loadingConfirm, setLoadingConfirm] = useState<boolean>(false);
  const [selectedTurma, setSelectedTurma] =
    useState<TurmaDisponivelInscricaoDetalhe | null>(null);

  const [inscricaoConfirmada, setInscricaoConfirmada] =
    useState<boolean>(false);
  const [turmaConfirmada, setTurmaConfirmada] =
    useState<TurmaDisponivelInscricaoDetalhe | null>(null);
  const [dataHoraInscricao, setDataHoraInscricao] = useState<string>("");

  const comprovanteRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const carregarDados = async () => {
      try {
        setLoading(true);
        setMsgError(null);

        if (!acaoId) {
          setMsgError("Acao nao informada.");
          return;
        }

        const [acaoResult, turmasResult] = await Promise.all([
          findAcaoQualificacao(acaoId),
          findTurmasDisponiveisAcao(acaoId),
        ]);

        if (!turmasResult?.success) {
          throw new Error("Nao foi possivel carregar as turmas disponiveis.");
        }

        setAcaoNome(acaoResult.nome || "");
        setTurmas(turmasResult?.data?.turmas || []);
      } catch (error: any) {
        setMsgError(
          error?.message || "Nao foi possivel carregar as turmas disponiveis.",
        );
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [acaoId]);

  const abrirConfirmacao = (turma: TurmaDisponivelInscricaoDetalhe) => {
    setSelectedTurma(turma);
    setOpenConfirm(true);
  };

  const confirmarInscricao = async () => {
    if (!selectedTurma) {
      return;
    }

    try {
      setLoadingConfirm(true);
      setMsgError(null);

      const result = await realizarInscricaoTurma(selectedTurma.id);

      if (!result.success) {
        throw new Error(
          result.message || "Nao foi possivel realizar a inscricao.",
        );
      }

      const dataHoraConfirmacao =
        result.data?.inscricao?.created_at ||
        result.data?.inscricao?.inscrito_em ||
        result.data?.created_at ||
        result.data?.inscrito_em ||
        result.created_at ||
        new Date().toISOString();

      setDataHoraInscricao(dataHoraConfirmacao);
      setTurmaConfirmada(selectedTurma);
      setInscricaoConfirmada(true);
      setOpenConfirm(false);
    } catch (error: any) {
      setMsgError(error?.message || "Nao foi possivel realizar a inscricao.");
      setOpenConfirm(false);
    } finally {
      setLoadingConfirm(false);
    }
  };

  const imprimirComprovante = () => {
    if (!turmaConfirmada) {
      return;
    }

    const enderecoTurma = montarEnderecoTurma(turmaConfirmada);
    const linkTurma = obterLinkComprovante(turmaConfirmada);
    const nomeInscrito = user?.nome_social?.trim() || user?.nome || "-";

    imprimirComprovanteInscricao({
      nomeInscrito,
      cpfInscrito: formatarCpf(user?.cpf) || "-",
      dataHoraInscricao: formatarDatetimeCompletoBr(dataHoraInscricao) || "-",
      nomeAcao: acaoNome || "-",
      nomeTurma: turmaConfirmada.nome,
      dataInicio: turmaConfirmada.data_inicio,
      dataFim: turmaConfirmada.data_fim,
      horaInicio: turmaConfirmada.hora_inicio,
      horaFim: turmaConfirmada.hora_fim,
      modalidade: turmaConfirmada.descricao_modalidade,
      cargaHoraria: turmaConfirmada.carga_horaria,
      endereco: enderecoTurma || null,
      link: linkTurma,
      observacoes: turmaConfirmada.observacoes,
    });
  };

  const salvarPdfComprovante = () => {
    if (!turmaConfirmada) {
      return;
    }

    const enderecoTurma = montarEnderecoTurma(turmaConfirmada);
    const linkTurma = obterLinkComprovante(turmaConfirmada);
    const nomeInscrito = user?.nome_social?.trim() || user?.nome || "-";

    salvarPdfComprovanteInscricao({
      nomeInscrito,
      cpfInscrito: formatarCpf(user?.cpf) || "-",
      dataHoraInscricao: formatarDatetimeCompletoBr(dataHoraInscricao) || "-",
      nomeAcao: acaoNome || "-",
      nomeTurma: turmaConfirmada.nome,
      dataInicio: turmaConfirmada.data_inicio,
      dataFim: turmaConfirmada.data_fim,
      horaInicio: turmaConfirmada.hora_inicio,
      horaFim: turmaConfirmada.hora_fim,
      modalidade: turmaConfirmada.descricao_modalidade,
      cargaHoraria: turmaConfirmada.carga_horaria,
      endereco: enderecoTurma || null,
      link: linkTurma,
      observacoes: turmaConfirmada.observacoes,
    });
  };

  const turmaParaConfirmacao = useMemo(
    () => turmaConfirmada,
    [turmaConfirmada],
  );

  if (loading) {
    return (
      <main className={stylesAcoes.page} aria-busy="true">
        <Skeleton variant="text" width={220} height={42} />
        <Skeleton variant="text" width={460} height={26} />
        {Array.from({ length: 3 }, (_, index) => (
          <section key={index} className={stylesAcoes.actionCard}>
            <Skeleton variant="text" width="45%" height={30} />
            <Skeleton height={130} />
          </section>
        ))}
      </main>
    );
  }

  if (inscricaoConfirmada && turmaParaConfirmacao) {
    const enderecoTurmaConfirmacao = montarEnderecoTurma(turmaParaConfirmacao);
    const nomeInscrito = user?.nome_social?.trim() || user?.nome || "-";

    return (
      <main className={stylesAcoes.page}>
        <div className={`${styles.acaoCard} ${stylesAcoes.actionCard}`}>
          <div ref={comprovanteRef}>
            <div className={styles.confirmTitle}>
              <CheckCircleOutline className={styles.successIcon} />
              <span>Inscrição Confirmada!</span>
            </div>

            <div className={styles.confirmInfoBox}>
              <div className={styles.confirmInfoNome}>{acaoNome || "-"}</div>

              <div className={styles.infoLineBlue}>
                <PersonOutline fontSize="small" />
                <span>
                  <strong>Nome do Participante: </strong>
                  {nomeInscrito}
                </span>
              </div>

              <div className={styles.infoLineBlue}>
                <BadgeOutlined fontSize="small" />
                <span>
                  <strong>CPF do Participante: </strong>
                  {formatarCpf(user?.cpf) || "-"}
                </span>
              </div>

              <div className={styles.infoLineBlue}>
                <CalendarMonthOutlined fontSize="small" />
                <span>
                  <strong>Data e hora da inscrição: </strong>
                  {formatarDatetimeCompletoBr(dataHoraInscricao) || "-"}
                </span>
              </div>

              <div className={styles.infoLineBlue}>
                <CalendarMonthOutlined fontSize="small" />
                <span>
                  <strong>Período da Turma: </strong>
                  {formatarDataDbParaBr(
                    turmaParaConfirmacao.data_inicio,
                  )} a {formatarDataDbParaBr(turmaParaConfirmacao.data_fim)}
                </span>
              </div>

              <div className={styles.infoLineBlue}>
                <SchoolOutlined fontSize="small" />
                <span>
                  <strong>Nome da Turma: </strong>
                  {turmaParaConfirmacao.nome}
                </span>
              </div>

              {turmaParaConfirmacao.hora_inicio &&
                turmaParaConfirmacao.hora_fim && (
                  <div className={styles.infoLineBlue}>
                    <AccessTimeOutlined fontSize="small" />
                    <span>
                      <strong>Horário da Turma: </strong>
                      {formatarHoraComprovante(
                        turmaParaConfirmacao.hora_inicio,
                      )}{" "}
                      às{" "}
                      {formatarHoraComprovante(turmaParaConfirmacao.hora_fim)}
                    </span>
                  </div>
                )}

              <div className={styles.infoLineBlue}>
                <SchoolOutlined fontSize="small" />
                <span>
                  <strong>Modalidade: </strong>
                  {turmaParaConfirmacao.descricao_modalidade || "-"}
                </span>
              </div>

              <div className={styles.infoLineBlue}>
                <ScheduleOutlined fontSize="small" />
                <span>
                  <strong>Carga Horária: </strong>
                  {turmaParaConfirmacao.carga_horaria ?? "-"} horas
                </span>
              </div>

              {!!enderecoTurmaConfirmacao && (
                <div className={styles.infoLineBlue}>
                  <LocationOnOutlined fontSize="small" />
                  <span>
                    <strong>Endereço: </strong>
                    {enderecoTurmaConfirmacao}
                  </span>
                </div>
              )}
            </div>

            <div className={styles.observacoesConfirmacao}>
              {turmaParaConfirmacao.observacoes?.trim() ||
                "Sem observações para esta turma."}
            </div>

            <div className={styles.confirmacaoTextoDestaque}>
              Sua inscrição foi realizada com sucesso! Caso precise, utilize os
              botões abaixo para imprimir ou salvar um PDF do seu comprovante de
              inscrição.
            </div>
          </div>

          <div className={styles.actionsCenter}>
            <Button
              variant="contained"
              className={styles_button.buttonSquarePrimary}
              startIcon={<PrintOutlined />}
              onClick={imprimirComprovante}
            >
              Imprimir comprovante
            </Button>

            <Button
              variant="outlined"
              className={styles.buttonOutlinePrimary}
              startIcon={<DownloadOutlined />}
              onClick={salvarPdfComprovante}
            >
              Salvar PDF
            </Button>

            <Button
              variant="contained"
              className={styles_button.buttonSquareSecondary}
              onClick={() => navigate("/gerenciar-inscricoes")}
            >
              Ver minhas inscrições
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={stylesAcoes.page}>
      <div className={stylesAcoes.header}>
        <h1>Escolher Turma</h1>
        <p>
          Selecione a turma para a qual deseja se inscrever. Atente-se as datas
          e as modalidades para participação quando houver mais de uma opção.
        </p>
      </div>

      <div>
        {msgError && (
          <div className={styles.errorWrapper}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        <div className={stylesAcoes.cardsList}>
          {turmas.length === 0 ? (
            <div className={styles.semResultados}>
              Nenhuma turma disponível para esta ação.
            </div>
          ) : (
            turmas.map((turma) => {
              const enderecoTurma = montarEnderecoTurma(turma);

              return (
                <article key={turma.id} className={stylesAcoes.actionCard}>
                  <div className={stylesAcoes.actionSummary}>
                    <div className={stylesAcoes.actionIcon}>
                      <SchoolOutlined />
                    </div>
                    <div className={stylesAcoes.actionContent}>
                      <span>Turma disponível</span>
                      <h2>{turma.nome}</h2>
                    </div>
                  </div>

                  <div className={stylesAcoes.turmasArea}>
                    <strong>Informações da turma</strong>
                    <div className={stylesAcoes.turmaRow}>
                      <CalendarMonthOutlined />
                      <span>
                        <strong>Período:</strong>{" "}
                        {formatarDataDbParaBr(turma.data_inicio)} a{" "}
                        {formatarDataDbParaBr(turma.data_fim)}
                      </span>
                    </div>
                    {turma.hora_inicio && turma.hora_fim && (
                      <div className={stylesAcoes.turmaRow}>
                        <AccessTimeOutlined />
                        <span>
                          <strong>Horário:</strong>{" "}
                          {formatarHoraComprovante(turma.hora_inicio)} às{" "}
                          {formatarHoraComprovante(turma.hora_fim)}
                        </span>
                      </div>
                    )}
                    <div className={stylesAcoes.turmaRow}>
                      <SchoolOutlined />
                      <span>
                        <strong>Modalidade:</strong>{" "}
                        {turma.descricao_modalidade || "-"}
                      </span>
                    </div>
                    <div className={stylesAcoes.turmaRow}>
                      <ScheduleOutlined />
                      <span>
                        <strong>Carga horária:</strong>{" "}
                        {turma.carga_horaria ?? "-"} horas
                      </span>
                    </div>
                    {enderecoTurma && (
                      <div className={stylesAcoes.turmaRow}>
                        <LocationOnOutlined />
                        <span>
                          <strong>Endereço:</strong> {enderecoTurma}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className={stylesAcoes.cardActions}>
                    <Button
                      variant="contained"
                      className={styles_button.buttonSoftPrimary}
                      onClick={() => abrirConfirmacao(turma)}
                    >
                      Selecionar turma
                    </Button>
                  </div>
                </article>
              );
            })
          )}
        </div>

        <ConfirmDialog
          open={openConfirm}
          loading={loadingConfirm}
          onClose={() => setOpenConfirm(false)}
          onConfirm={confirmarInscricao}
          title="Confirmar inscrição"
          message={`Deseja confirmar sua inscrição na turma "${selectedTurma?.nome || ""}"?`}
          titleButtonConfirm="Confirmar inscrição"
          titleLoadButton="Confirmando..."
        />

        <div className={styles.actionsCenter}>
          <Button
            variant="contained"
            className={styles_button.buttonSquareSecondary}
            onClick={() => navigate(`/orientacao-acao?id=${acaoId ?? ""}`)}
          >
            Voltar
          </Button>
        </div>
      </div>
    </main>
  );
};

export default EscolherTurma;
